import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import useReconnectErrorNotification from '@/hooks/device/useReconnectErrorNotification';
import mwConnectionService from '@/services/mw/mwConnectionService';
import mwIotService from '@/services/mw/mwIotService';
import mwStateService from '@/services/mw/mwStateService';
import {selectIsHolderRegistered} from '@/state/selectors/connectedProduct';
import {
    selectIsConnectingToIot,
    selectIsDeviceConnectedToIot,
    selectIsHolderConnectingToIot,
} from '@/state/selectors/device';
import {selectIsHolderConnected, selectIsHolderInitialized} from '@/state/selectors/mw';
import {showHolderRegistrationModalAction} from '@/state/slices/modalSlice';
import {dispatch} from '@/state/store';

const useHolderDetect = () => {
    const isDeviceConnectedToIot = useSelector(selectIsDeviceConnectedToIot);
    const isConnectingToIot = useSelector(selectIsConnectingToIot);
    const isHolderConnectingToIot = useSelector(selectIsHolderConnectingToIot);
    const isHolderConnected = useSelector(selectIsHolderConnected);
    const isReconnectCanBeStarted = isDeviceConnectedToIot && !isConnectingToIot && isHolderConnected;
    const isHolderInitialized = useSelector(selectIsHolderInitialized);
    const isHolderRegistered = useSelector(selectIsHolderRegistered);
    const [isReconnectInProgress, setIsReconnectInProgress] = useState(false);
    const [lastIsHolderConnected, setLastIsHolderConnected] = useState(isHolderConnected);
    const showReconnectError = useReconnectErrorNotification();

    const startHolderReconnect = () => {
        setIsReconnectInProgress(true);
    };

    const stopHolderReconnect = () => {
        setIsReconnectInProgress(false);
    };

    const onError = () => {
        mwStateService.clearHolderState();

        showReconnectError();

        stopHolderReconnect();
    };

    useEffect(() => {
        //to ignore holder detect on connection init
        if (!isHolderInitialized || !isReconnectCanBeStarted) return;

        if (isHolderRegistered) {
            //if registered start sync
            mwIotService.getUamHolderDataForConnectedDevice({onSuccess: stopHolderReconnect, onError});
        } else {
            //while no more reconnect in progress reset it and show holder reg popup
            stopHolderReconnect();
            dispatch(showHolderRegistrationModalAction({isHolderReconnect: true}));
        }
    }, [isHolderInitialized]);

    useEffect(() => {
        //wait while holder registered
        //no any another reconnect at same time
        //if holder not connected then holder state will be cleared automatically and holder reg popup will be closed
        if (!isHolderRegistered || isReconnectInProgress || !isReconnectCanBeStarted) return;

        startHolderReconnect();
    }, [isHolderRegistered]);

    useEffect(() => {
        //start reconnect process should be true to start or continue reconnect
        if (!isReconnectInProgress) return;

        if (isHolderInitialized) {
            mwIotService.getUamHolderDataForConnectedDevice({onSuccess: stopHolderReconnect, onError});
        } else {
            //onSuccess not needed because state with holder codentify is updated later than onSuccess
            //so isHolderInitialized have been used
            mwConnectionService.initHolderConnection({onError});
        }
    }, [isReconnectInProgress]);

    useEffect(() => {
        //last known status already same
        if (lastIsHolderConnected === isHolderConnected) return;

        if (!isHolderConnected) {
            setLastIsHolderConnected(false);
            stopHolderReconnect();
            mwStateService.clearHolderState();
            return;
        }

        if (isHolderConnectingToIot && isHolderConnected) {
            //setup first holder init
            setLastIsHolderConnected(true);
            return;
        }

        //no any another reconnect at same time
        if (!isReconnectCanBeStarted || isReconnectInProgress) return;

        setLastIsHolderConnected(true);
        startHolderReconnect();
    }, [isHolderConnected, isReconnectCanBeStarted, isReconnectInProgress]);
};

export default useHolderDetect;
